import type { MouseEvent, ReactNode } from 'react'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import TaActionButton from '../TaActionButton'
import { useAppActions } from '../../AppContext'
import { addToast } from '../../services/Toasts'
import { useQuestionProcessing } from '../../services/TaskManagerService'
import { runScore } from '../../services/QuestionActions'
import { ICON_SM } from '../../common/theme'
import type { Question } from '../../types'
import { hasAnswerText } from '../../common/common'

interface GenerateScoringButtonProps {
  question: Question
  icon?: ReactNode
  disabled?: boolean
  iconSize?: number
  sx?: any
  testId?: string
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

// Toolbar button that fires the per-question score estimation. Mirrors
// GenerateTraceButton: owns the click → runScore → result-toast flow
// internally so callers just drop it next to a question. Spins from the
// shared TaskManagerService processing registry (runScore flips the
// `scoring` flag for us). Optional overrides let row contexts customise
// the icon, sizing and hover handlers without re-implementing the
// click pipeline.
export default function GenerateScoringButton({
  question, icon, disabled, iconSize, sx, testId, onMouseEnter, onMouseLeave,
}: GenerateScoringButtonProps) {
  const { updateQuestion, getCurrentProject } = useAppActions()
  const processing = useQuestionProcessing(question.questionsha)
  const hasAnswer = hasAnswerText(question)

  const handleClick = async (e?: MouseEvent) => {
    e?.stopPropagation()
    const project = getCurrentProject()
    if (!project) return
    const r = await runScore(question, project, updateQuestion)
    if (r.success) addToast('success', 'Score estimated')
    else addToast('error', r.msg ?? 'Failed to estimate score')
  }

  const queued = processing.isqueuedscoring
  const running = processing.isrunningscoring
  return (
    <TaActionButton
      title={queued ? 'Queued' : 'Estimate Score'}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={!hasAnswer || !!disabled}
      running={running}
      queued={queued}
      icon={icon ?? <AssessmentOutlinedIcon sx={{ fontSize: ICON_SM }} />}
      iconSize={iconSize}
      sx={sx}
      testId={testId}
    />
  )
}
